// Klienti BC — seznam lidí, kteří si koupili bootcamp (7.9.2026, zadání Ládi).
//
// ZÁMĚRNĚ ODDĚLENÁ EVIDENCE. Nic se netahá z karty klientky koučinku: žádné
// permice, tréninky, stav spolupráce ani její kontakt. Jméno, e-mail a telefon
// jsou ty, které člověk uvedl při nákupu bootcampu, a zůstanou takové, i kdyby
// si je v koučinku později změnil. Faktura je vždycky jen ta za tenhle bootcamp.
//
// V databázi je řádek na PŘIHLÁŠKU, ne na osobu — kdo jede dva běhy, má dva
// řádky. Server je slučuje podle e-mailu, a když chybí, podle telefonu.
//
// 16.9.2026 (odsouhlasený mockup): detail má stejný tvar jako detail klienta
// koučinku — tmavá hlavička, čtyři dlaždice s čísly, pod nimi sbalitelné karty.
// Komentáře jsou první, protože kvůli nim se detail nejčastěji otevírá, a jeden
// z nich jde připnout nahoru. Telefon a e-mail jsou tlačítka, klepnutí kopíruje,
// a řetízek v hlavičce zkopíruje odkaz na tuhle klientku pro někoho z týmu.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Search, RefreshCw, X, StickyNote, Users, Send, Trash2, Phone, Mail, Copy, Check,
  Link2, Pin, PinOff, ChevronDown, MessageSquare, CalendarCheck, Receipt, Ticket,
} from "lucide-react";
import { SkeletonRows, EmptyState, Stitek } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice } from "../../components/PrehledDlazdice.jsx";
import KpiCard from "../../components/KpiCard.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { formatDate as fmtDate } from "../../lib/format.js";
import { getCurrentUser } from "../../lib/auth.js";
import { BonusoveVstupyBlok, PilulkaBonus } from "../../components/BonusoveVstupy.jsx";

const PLATBA_STYL = {
  zaplaceno: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "ceka na platbu": "bg-amber-50 text-amber-800 border-amber-200",
  "po splatnosti": "bg-rose-50 text-rose-800 border-rose-200",
  "bez faktury": "bg-slate-100 text-slate-600 border-slate-200",
};
const PLATBA_TEXT = {
  zaplaceno: "zaplaceno",
  "ceka na platbu": "čeká na platbu",
  "po splatnosti": "po splatnosti",
  "bez faktury": "bez faktury",
};

const DOCHAZKA_STYL = {
  dorazila: "bg-emerald-50 text-emerald-800 border-emerald-200",
  nedorazila: "bg-rose-50 text-rose-800 border-rose-200",
  propadla: "bg-amber-50 text-amber-800 border-amber-200",
  rezervovano: "bg-brand-50 text-brand-800 border-brand-200",
};
const DOCHAZKA_TEXT = {
  dorazila: "dorazila",
  nedorazila: "nedorazila",
  propadla: "propadlo",
  rezervovano: "přihlášená",
};

const DNY_ZKRATKA = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

function Platba({ stav }) {
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${PLATBA_STYL[stav] || PLATBA_STYL["bez faktury"]}`}>
      {PLATBA_TEXT[stav] || stav}
    </span>
  );
}

function bezDiakritiky(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function iniciály(jmeno) {
  return String(jmeno || "?").trim().split(/\s+/).slice(0, 2).map((x) => x.charAt(0).toUpperCase()).join("");
}

// "13. 8. 14:55" — stejný tvar jako u komentářů ke spolupráci.
function kdyText(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getDate()}. ${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function telCz(t) {
  const d = String(t || "").replace(/\D/g, "").slice(-9);
  return d.length === 9 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : String(t || "");
}

// "Út 15. 9. 2026" — den v týdnu napřed, ať se docházka čte na první pohled.
function datumSDnem(iso) {
  const d = new Date(`${iso}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return fmtDate(iso);
  return `${DNY_ZKRATKA[d.getDay()]} ${fmtDate(iso)}`;
}

// Kolik dní zbývá do data (kladné číslo), null když datum chybí.
function dniDo(iso) {
  if (!iso) return null;
  const cil = new Date(`${iso}T12:00:00`);
  if (!Number.isFinite(cil.getTime())) return null;
  const dnes = new Date();
  return Math.round((cil - new Date(dnes.getFullYear(), dnes.getMonth(), dnes.getDate(), 12)) / 86400000);
}

function sklonujTrenink(n) {
  return n === 1 ? "trénink" : n < 5 ? "tréninky" : "tréninků";
}

function dnesIso() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague" }).format(new Date());
}

const MESIC_ZKRATKA = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čvn", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
const MESIC_PLNY = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

// Tréninky po měsících, stejný tvar jako u klienta koučinku (16.9.2026).
// Číslo v buňce = kolikrát v tom měsíci dorazila. Oranžový odznak = vstupy,
// které jí v tom měsíci propadly, to je to, co stojí za pohled.
function HeatmapaDochazky({ dochazka, rok, roky, onRok }) {
  const dorazila = Array(12).fill(0);
  const propadlo = Array(12).fill(0);
  for (const d of dochazka) {
    const m = new Date(`${d.datum}T12:00:00`);
    if (!Number.isFinite(m.getTime()) || m.getFullYear() !== rok) continue;
    if (d.stav === "dorazila") dorazila[m.getMonth()] += 1;
    if (d.stav === "propadla") propadlo[m.getMonth()] += 1;
  }
  const max = Math.max(...dorazila, 1);
  const dnes = new Date();
  const aktualni = rok === dnes.getFullYear() ? dnes.getMonth() : (rok < dnes.getFullYear() ? 11 : -1);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-2.5 flex items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tréninky po měsících</div>
        {roky.length > 1 && (
          <select value={rok} onChange={(e) => onRok(parseInt(e.target.value, 10))}
            className="ml-auto rounded-lg border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
            {roky.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
      </div>
      <div className="grid grid-cols-6 gap-2 sm:grid-cols-12 sm:gap-1.5">
        {dorazila.map((v, i) => {
          const podil = v / max;
          const styl = v === 0 ? "bg-slate-50 text-slate-300"
            : podil > 0.8 ? "bg-emerald-500 text-white"
            : podil > 0.6 ? "bg-emerald-400 text-white"
            : podil > 0.4 ? "bg-emerald-300 text-emerald-900"
            : podil > 0.2 ? "bg-emerald-200 text-emerald-900"
            : "bg-emerald-100 text-emerald-800";
          const budouci = i > aktualni;
          const prsten = i === aktualni ? "ring-2 ring-offset-1 ring-amber-400" : "";
          const popis = [`${MESIC_PLNY[i]}: ${v} ${sklonujTrenink(v)}`];
          if (propadlo[i]) popis.push(`${propadlo[i]} propadlo`);
          return (
            <div key={i} className="relative min-w-0 text-center">
              {propadlo[i] > 0 && (
                <div className="absolute -top-1.5 right-0 z-10 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold tabular-nums text-white shadow-md ring-2 ring-white"
                  title={`${propadlo[i]} propadlých vstupů`}>
                  {propadlo[i]}
                </div>
              )}
              <div title={popis.join(", ")}
                className={`flex h-[46px] items-center justify-center rounded-xl text-base font-bold tabular-nums sm:h-11 sm:rounded-lg ${styl} ${prsten} ${budouci ? "opacity-40" : ""}`}>
                {v || "—"}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{MESIC_ZKRATKA[i]}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-emerald-300" /> Kolikrát dorazila</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full bg-amber-500" /> Propadlé vstupy</span>
      </div>
    </div>
  );
}

// ── Drobné stavební kameny detailu ───────────────────────────────────────────

/** Kontakt v hlavičce jako tlačítko: klepnutí zkopíruje hodnotu do schránky. */
function CipKontaktu({ ikona: Ikona, hodnota, titulek, cislice = false }) {
  const [hotovo, setHotovo] = useState(false);
  const kopiruj = async () => {
    try {
      await navigator.clipboard.writeText(String(hodnota));
      setHotovo(true);
      setTimeout(() => setHotovo(false), 1500);
    } catch (_) { /* schránka není k dispozici, ticho */ }
  };
  return (
    <button
      type="button"
      onClick={kopiruj}
      title={titulek}
      className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-2.5 py-1 text-[12.5px] text-white transition active:scale-95 sm:hover:bg-white/20"
    >
      <Ikona className="h-3.5 w-3.5 shrink-0 text-white/70" />
      <span className={`min-w-0 truncate ${cislice ? "font-semibold tabular-nums" : ""}`}>{hodnota}</span>
      {hotovo ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-300" /> : <Copy className="h-3.5 w-3.5 shrink-0 text-white/50" />}
    </button>
  );
}

/** Ikonové tlačítko v tmavé hlavičce okna. */
function IkonaVHlavicce({ ikona: Ikona, popis, onClick, hotovo = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={popis}
      aria-label={popis}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:hover:bg-white/20"
    >
      {hotovo ? <Check className="h-5 w-5 text-emerald-300" /> : <Ikona className="h-5 w-5" />}
    </button>
  );
}

/** Jeden trénink v docházce. Budoucí je světlejší, ještě se nestal. */
function RadekDochazky({ d, budouci = false }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 ${budouci ? "border-brand-200 bg-brand-50/40" : "border-slate-200 bg-slate-50"}`}>
      <div className="min-w-0">
        <div className="text-sm font-bold tabular-nums text-slate-800">
          {datumSDnem(d.datum)}{d.cas_od ? `, ${d.cas_od}` : ""}
        </div>
        <div className="text-[11.5px] text-slate-500">
          {[d.misto || d.beh, d.trener || null, d.zruseno ? "lekce zrušena" : null].filter(Boolean).join(", ")}
        </div>
      </div>
      <span className={`ml-auto inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${DOCHAZKA_STYL[d.stav] || PLATBA_STYL["bez faktury"]}`}>
        {DOCHAZKA_TEXT[d.stav] || d.stav}
      </span>
    </div>
  );
}

/** Sbalitelná karta v detailu. Lišta má název, počet a šipku. */
function KartaDetailu({ ikona: Ikona, nazev, pocet, popis, otevreno, onToggle, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div
        className="flex cursor-pointer select-none items-center gap-2 bg-brand-light px-4 py-2.5"
        onClick={onToggle}
      >
        {Ikona && <Ikona className="h-4 w-4 shrink-0 text-brand-deep" />}
        <h3 className="text-sm font-bold text-brand-deep">{nazev}</h3>
        {pocet != null && (
          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums text-brand-deep">{pocet}</span>
        )}
        {popis && <span className="hidden truncate text-[11.5px] text-slate-500 sm:block">{popis}</span>}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-expanded={!!otevreno}
          aria-label={otevreno ? "Sbalit" : "Rozbalit"}
          className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-brand-deep transition active:scale-95 sm:hover:bg-white/70"
        >
          <ChevronDown className={`h-5 w-5 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
        </button>
      </div>
      {otevreno && <div className="p-4">{children}</div>}
    </div>
  );
}

// ── Detail jednoho člověka ───────────────────────────────────────────────────
function DetailKlienta({ klic, onClose, onZmena }) {
  const { data, loading, error, refetch } = useApiData(`/api/bootcampy?klient=${encodeURIComponent(klic)}`);
  const k = data && data.klient;
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [chyba, setChyba] = useState("");
  const [odkazZkopirovan, setOdkazZkopirovan] = useState(false);
  const [otevrene, setOtevrene] = useState({ komentare: true, permanentky: true, dochazka: true, faktury: false });
  const [vseDochazka, setVseDochazka] = useState(false);
  const [rokHeat, setRokHeat] = useState(() => new Date().getFullYear());
  const ja = getCurrentUser();
  const jeOwner = String(ja?.role || "").toLowerCase() === "owner";
  const prepni = (klicKarty) => setOtevrene((o) => ({ ...o, [klicKarty]: !o[klicKarty] }));

  // Zavírání klávesou Esc, ať se okno nemusí trefovat myší.
  useEffect(() => {
    // Otevřené okno nad detailem (bonusový vstup) si Esc zavře samo, detail zůstane.
    const naKlavesu = (e) => { if (e.key === "Escape" && !document.querySelector("[role=dialog][aria-modal=true]")) onClose(); };
    window.addEventListener("keydown", naKlavesu);
    return () => window.removeEventListener("keydown", naKlavesu);
  }, [onClose]);

  const odesli = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true); setChyba("");
    try {
      const r = await apiFetch("/api/bootcampy", {
        method: "POST",
        body: JSON.stringify({ akce: "komentar-pridat", klic, text }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Odeslání selhalo");
      setDraft("");
      await refetch();
      if (onZmena) onZmena();
    } catch (e) { setChyba(e.message || "Odeslání selhalo"); }
    setPosting(false);
  };

  const smaz = async (id) => {
    setChyba("");
    try {
      const r = await apiFetch("/api/bootcampy", {
        method: "POST",
        body: JSON.stringify({ akce: "komentar-smazat", id }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Smazání selhalo");
      await refetch();
      if (onZmena) onZmena();
    } catch (e) { setChyba(e.message || "Smazání selhalo"); }
  };

  // Připnutý je vždycky nejvýš jeden, server ostatní odepne sám.
  const pripni = async (id, pripnuto) => {
    setChyba("");
    try {
      const r = await apiFetch("/api/bootcampy", {
        method: "POST",
        body: JSON.stringify({ akce: "komentar-pripnout", id, pripnuto }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Připnutí selhalo");
      await refetch();
      if (onZmena) onZmena();
    } catch (e) { setChyba(e.message || "Připnutí selhalo"); }
  };

  const kopirujOdkaz = async () => {
    try {
      const url = `${window.location.origin}/bootcampy/klienti?klient=${encodeURIComponent(klic)}`;
      await navigator.clipboard.writeText(url);
      setOdkazZkopirovan(true);
      setTimeout(() => setOdkazZkopirovan(false), 1800);
    } catch (_) { /* schránka není k dispozici, ticho */ }
  };

  const s = (k && k.souhrn) || {};
  const zivy = k && k.behy && k.behy[0] ? k.behy[0] : null;
  const dochazka = (k && k.dochazka) || [];
  // Budoucí termíny nahoru, historie pod ně. Dokud byly v jednom seznamu,
  // nešlo poznat, co má žena zamluvené a co už odchodila (16.9.2026).
  const dnes = dnesIso();
  const budouci = dochazka.filter((d) => d.datum >= dnes).slice().reverse();
  const historie = dochazka.filter((d) => d.datum < dnes);
  const historieVidet = vseDochazka ? historie : historie.slice(0, 3);
  const rokyDochazky = [...new Set(dochazka.map((d) => Number(String(d.datum).slice(0, 4))).filter(Boolean))].sort((a, b) => b - a);
  const zbyvaDni = dniDo(s.platnost_do);

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {loading && !k && <div className="p-8 text-center text-sm text-slate-400">Načítám…</div>}
        {/* Bez tohohle bylo okno při chybě prázdné a vypadalo to jako rozbitá
            appka. Radši řekni, co se stalo, a nabídni zavření (16.9.2026). */}
        {!loading && !k && (
          <div className="p-8 text-center">
            <div className="text-sm font-semibold text-slate-700">Detail se nepodařilo načíst</div>
            <div className="mt-1 text-xs text-slate-500">{String(error || "Zkus to prosím znovu.")}</div>
            <div className="mt-4 flex justify-center gap-2">
              <button type="button" onClick={() => refetch(true)} className="btn btn-secondary">Zkusit znovu</button>
              <button type="button" onClick={onClose} className="btn btn-secondary">Zavřít</button>
            </div>
          </div>
        )}
        {k && (
          <>
            {/* Hlavička ve stejném tvaru jako u klienta koučinku. */}
            <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3.5 text-white sm:px-5">
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/25 bg-white/15 font-bold">
                  {iniciály(k.jmeno)}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-serif text-lg font-bold leading-tight sm:text-xl">{k.jmeno}</h2>
                  {zivy && (
                    <div className="truncate text-[11.5px] text-white/70">
                      {[zivy.misto || zivy.nazev, zivy.tarif || null, zivy.prihlasena ? `od ${fmtDate(zivy.prihlasena)}` : null]
                        .filter(Boolean).join(", ")}
                    </div>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {k.telefon && <CipKontaktu ikona={Phone} hodnota={telCz(k.telefon)} titulek="Zkopírovat telefon" cislice />}
                    {k.email && <CipKontaktu ikona={Mail} hodnota={k.email} titulek="Zkopírovat e-mail" />}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <IkonaVHlavicce ikona={Link2} popis="Zkopírovat odkaz na klientku" onClick={kopirujOdkaz} hotovo={odkazZkopirovan} />
                  <IkonaVHlavicce ikona={X} popis="Zavřít" onClick={onClose} />
                </div>
              </div>
            </div>

            <div className="space-y-4 bg-brand-cream/60 p-4 sm:p-5">
              {/* Čtyři čísla, kvůli kterým se detail otevírá. */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard
                  label="Lokalita"
                  accent="brand"
                  value={zivy ? (zivy.misto || zivy.nazev) : "—"}
                  sub={zivy && zivy.trener ? zivy.trener : (k.pocetBehu > 1 ? `${k.pocetBehu} přihlášky` : undefined)}
                />
                <KpiCard
                  label="Zbývá vstupů"
                  accent={s.zbyva === 0 ? "red" : "green"}
                  value={s.vstupy == null ? "bez omezení" : s.zbyva}
                  sub={s.vstupy == null ? undefined : `z ${s.vstupy}${zivy && zivy.tarif ? `, ${zivy.tarif}` : ""}`}
                />
                <KpiCard
                  label="Vyčerpat do"
                  accent="amber"
                  value={s.platnost_do ? fmtDate(s.platnost_do) : "neurčeno"}
                  sub={zbyvaDni == null ? undefined : zbyvaDni < 0 ? "platnost vypršela" : `zbývá ${zbyvaDni} dní`}
                />
                <KpiCard
                  label="Zaplaceno"
                  accent="blue"
                  value={`${Number(s.zaplaceno || 0).toLocaleString("cs-CZ")} Kč`}
                  sub={`${k.faktury.length} ${k.faktury.length === 1 ? "faktura" : k.faktury.length < 5 ? "faktury" : "faktur"}`}
                />
              </div>

              {/* 1. Komentáře — kvůli nim se detail otevírá nejčastěji. */}
              <KartaDetailu
                ikona={MessageSquare}
                nazev="Komentáře"
                pocet={(k.komentare || []).length}
                popis="interní, napříč všemi běhy"
                otevreno={otevrene.komentare}
                onToggle={() => prepni("komentare")}
              >
                <div className="space-y-2">
                  {(k.komentare || []).map((c) => {
                    const muj = jeOwner || (ja?.email && String(c.autorEmail || "").toLowerCase() === String(ja.email).toLowerCase());
                    return (
                      <div
                        key={c.id}
                        className={`group flex gap-2.5 rounded-xl border p-3 ${
                          c.pripnuto ? "border-brand-200 bg-brand-50/60 border-l-4 border-l-brand" : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-brand-200 bg-brand-50 text-[11px] font-bold text-brand-700">
                          {iniciály(c.autor)}
                        </div>
                        <div className="min-w-0 flex-1">
                          {c.pripnuto && (
                            <div className="mb-0.5 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-brand-deep">
                              <Pin className="h-3 w-3" /> Připnuto
                            </div>
                          )}
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-[12.5px] font-bold text-slate-800">{c.autor || "—"}</span>
                            <span className="text-[11px] text-slate-400">{kdyText(c.kdy)}</span>
                          </div>
                          <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-700">{c.text}</div>
                        </div>
                        <div className="flex shrink-0 items-start gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                          <button
                            onClick={() => pripni(c.id, !c.pripnuto)}
                            title={c.pripnuto ? "Odepnout" : "Připnout nahoru"}
                            aria-label={c.pripnuto ? "Odepnout komentář" : "Připnout komentář nahoru"}
                            className={`rounded-lg p-1 transition ${c.pripnuto ? "text-brand" : "text-slate-300 hover:bg-brand-50 hover:text-brand"}`}
                          >
                            {c.pripnuto ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                          </button>
                          {muj && (
                            <button
                              onClick={() => smaz(c.id)}
                              title="Smazat komentář"
                              aria-label="Smazat komentář"
                              className="rounded-lg p-1 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(k.komentare || []).length === 0 && (
                    <div className="py-1 text-xs text-slate-400">Zatím nikdo nic nenapsal.</div>
                  )}
                </div>

                {/* Enter odešle, Shift+Enter nový řádek — stejně jako u klientky. */}
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-2.5 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100">
                  <div className="flex items-end gap-2">
                    <textarea value={draft} rows={1} maxLength={2000} disabled={posting}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); odesli(); } }}
                      onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`; }}
                      placeholder="Napiš komentář ke klientce…"
                      className="min-w-0 flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:opacity-50"
                      style={{ maxHeight: 150 }} />
                    <button type="button" onClick={odesli} disabled={!draft.trim() || posting}
                      title="Odeslat (Enter)" aria-label="Odeslat komentář"
                      className={`h-10 w-10 shrink-0 grid place-items-center rounded-full text-white transition ${
                        !draft.trim() || posting ? "bg-slate-200 cursor-not-allowed" : "bg-brand-600 hover:bg-brand-700 active:scale-95"}`}>
                      {posting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="ml-1 mt-1 text-[11px] text-slate-400 hidden sm:block">
                    <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[11px]">Enter</kbd> odešle,{" "}
                    <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[11px]">Shift + Enter</kbd> nový řádek
                  </div>
                </div>
                {chyba && <div className="mt-2 text-xs text-rose-700">{chyba}</div>}

                {/* Ruční poznámky z přihlášek zůstávají vidět; ty, které si píše
                    párovač faktur, jsou tišší — nikdo je nepsal. */}
                {(k.poznamkyPrihlasek || []).map((p, i) => (
                  <div key={i} className={`mt-2 text-xs border-l-2 pl-2.5 ${p.automaticka ? "text-slate-400 border-slate-200" : "text-slate-600 border-brand-200"}`}>
                    <b className={p.automaticka ? "text-slate-500" : "text-slate-700"}>Z přihlášky na {p.beh}:</b> {p.text}
                  </div>
                ))}
              </KartaDetailu>

              {/* 2. Permanentky a bonusové vstupy (1. 11. 2026, hlášení 191). */}
              <KartaDetailu
                ikona={Ticket}
                nazev="Permanentky"
                pocet={(k.permanentky || []).length}
                popis="bonus se přičte k aktivní permanentce"
                otevreno={otevrene.permanentky}
                onToggle={() => prepni("permanentky")}
              >
                {(k.permanentky || []).length === 0 ? (
                  <div className="text-xs text-slate-400">Zatím žádná permanentka.</div>
                ) : (
                  <div className="space-y-2">
                    {k.permanentky.map((p) => {
                      const propadla = p.platnost_do && p.platnost_do < dnes;
                      const aktivni = k.bonusCil && k.bonusCil.permanentka_id === p.id;
                      return (
                        <div key={p.id} className={`rounded-xl border p-3 ${aktivni ? "border-brand-200 bg-brand-50/50" : "border-slate-200 bg-slate-50"}`}>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-sm font-bold text-slate-800">{p.tarif || "Permanentka"}</span>
                            {aktivni && <Stitek ton="znacka" velikost="mala">aktivní</Stitek>}
                            {k.behy.length > 1 && <span className="text-[11.5px] text-slate-500">{p.beh}</span>}
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {p.vstupu == null ? (
                              <Stitek ton="neutralni" velikost="mala">bez omezení</Stitek>
                            ) : (
                              <>
                                <Stitek ton="neutralni" velikost="mala">{p.vstupu_zakoupeno} koupeno</Stitek>
                                <PilulkaBonus pocet={p.bonus} />
                                <Stitek ton={p.pouzito >= p.vstupu ? "problem" : "ok"} velikost="mala">
                                  vyčerpáno {p.pouzito} z {p.vstupu}
                                </Stitek>
                              </>
                            )}
                            {!p.zaplaceno ? (
                              <Stitek ton="pozor" velikost="mala">nezaplaceno</Stitek>
                            ) : (
                              <Stitek ton={propadla ? "problem" : "neutralni"} velikost="mala">
                                {p.platnost_do ? `${propadla ? "propadla" : "platí do"} ${fmtDate(p.platnost_do)}` : "bez data konce"}
                              </Stitek>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <BonusoveVstupyBlok
                  className="mt-3"
                  cil={k.bonusCil}
                  bonusy={k.bonusy || []}
                  smiBonus={!!(data && data.smiBonus)}
                  smiSmazat={!!(data && data.smiSmazatBonus)}
                  jmeno={k.jmeno}
                  onZmena={async () => { await refetch(true); if (onZmena) onZmena(); }}
                />
              </KartaDetailu>

              {/* 3. Docházka po jednotlivých trénincích. */}
              <KartaDetailu
                ikona={CalendarCheck}
                nazev="Docházka"
                pocet={dochazka.length}
                popis={[s.dorazila ? `${s.dorazila}× dorazila` : null, budouci.length ? `${budouci.length} zamluveno` : null].filter(Boolean).join(", ") || undefined}
                otevreno={otevrene.dochazka}
                onToggle={() => prepni("dochazka")}
              >
                {dochazka.length === 0 ? (
                  <div className="text-xs text-slate-400">Zatím žádný zapsaný trénink.</div>
                ) : (
                  <div className="space-y-4">
                    <HeatmapaDochazky
                      dochazka={dochazka}
                      rok={rokHeat}
                      roky={rokyDochazky}
                      onRok={setRokHeat}
                    />

                    {budouci.length > 0 && (
                      <div>
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Zamluvené termíny, {budouci.length}
                        </div>
                        <div className="space-y-2">
                          {budouci.map((d) => <RadekDochazky key={d.id} d={d} budouci />)}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Odchozené, {historie.length}
                      </div>
                      {historie.length === 0 ? (
                        <div className="text-xs text-slate-400">Zatím žádný proběhlý trénink.</div>
                      ) : (
                        <div className="space-y-2">
                          {historieVidet.map((d) => <RadekDochazky key={d.id} d={d} />)}
                          {historie.length > 3 && (
                            <button
                              type="button"
                              onClick={() => setVseDochazka((v) => !v)}
                              className="w-full rounded-xl border border-slate-200 py-2 text-xs font-semibold text-brand-deep transition sm:hover:bg-brand-50"
                            >
                              {vseDochazka ? "Zobrazit jen poslední tři" : `Zobrazit všech ${historie.length} ${sklonujTrenink(historie.length)}`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </KartaDetailu>

              {/* 4. Faktury — bude jich přibývat, proto sbalené. */}
              <KartaDetailu
                ikona={Receipt}
                nazev="Faktury"
                pocet={k.faktury.length}
                otevreno={otevrene.faktury}
                onToggle={() => prepni("faktury")}
              >
                {k.faktury.length === 0 ? (
                  <div className="text-xs text-slate-400">Zatím žádná napárovaná faktura.</div>
                ) : (
                  <div className="space-y-2">
                    {k.faktury.map((f, i) => (
                      <div key={`${f.cislo}-${i}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div>
                          <div className="text-sm font-bold tabular-nums text-slate-800">
                            {f.cislo || "bez čísla"}{f.castka != null ? `, ${f.castka.toLocaleString("cs-CZ")} Kč` : ""}
                          </div>
                          <div className="text-[11.5px] text-slate-500">
                            {f.beh}{f.tarif ? `, tarif ${f.tarif}` : ""}{f.ucet ? `, účet ${f.ucet}` : ""}
                          </div>
                        </div>
                        <div className="ml-auto flex flex-wrap items-center gap-3">
                          <span className="text-[11.5px] text-slate-500">
                            {f.uhrazeno ? `uhrazeno ${fmtDate(f.uhrazeno)}` : f.splatnost ? `splatnost ${fmtDate(f.splatnost)}` : ""}
                          </span>
                          <Platba stav={f.platba} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </KartaDetailu>

              {/* Druhá a další přihláška se ukáže jen tomu, kdo ji má. */}
              {k.behy.length > 1 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Všechny přihlášky, {k.behy.length}
                  </div>
                  <div className="space-y-2">
                    {k.behy.map((b) => (
                      <div key={b.ucastnice_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-slate-800">{b.nazev}</div>
                          <div className="text-[11.5px] text-slate-500">
                            {fmtDate(b.od)}{b.do ? ` – ${fmtDate(b.do)}` : " – bez konce"}
                            {b.misto ? `, ${b.misto}` : ""}{b.trener ? `, ${b.trener}` : ""}
                          </div>
                        </div>
                        <div className="ml-auto flex flex-wrap items-center gap-3">
                          <span className="text-[11.5px] tabular-nums text-slate-600">
                            {b.tarif ? `${b.tarif}, ` : ""}
                            {b.vstupy == null ? "bez omezení" : `vyčerpáno ${b.cerpano} / ${b.vstupy}`}
                          </span>
                          <Platba stav={b.platba} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Seznam ───────────────────────────────────────────────────────────────────
export default function KlientiBC() {
  const { data, loading, refetch, refreshing } = useApiData("/api/bootcampy?klienti=1");
  const [search, setSearch] = useState("");
  const [filtrPlatba, setFiltrPlatba] = useState("");
  const [filtrLokalita, setFiltrLokalita] = useState("");
  // Odkaz na klientku z hlavičky detailu: /bootcampy/klienti?klient=<klic>.
  // Kdo ho dostane, otevře rovnou její detail (16.9.2026).
  const [parametry, nastavParametry] = useSearchParams();
  const otevreny = parametry.get("klient") || null;
  const otevri = (klic) => {
    const p = new URLSearchParams(parametry);
    if (klic) p.set("klient", klic); else p.delete("klient");
    nastavParametry(p, { replace: true });
  };
  const uzka = useUzkaObrazovka();

  const klienti = data && data.klienti ? data.klienti : [];
  // Trenérovi server posílá jen lidi z jeho běhů — ať to na obrazovce ví.
  const jenMujBeh = !!(data && data.jenMujBeh);

  const lokality = useMemo(
    () => [...new Set(klienti.flatMap((k) => k.lokality || []))].sort((a, b) => a.localeCompare(b, "cs")),
    [klienti]
  );

  const filtrovani = useMemo(() => {
    const q = bezDiakritiky(search.trim());
    return klienti.filter((k) => {
      if (filtrPlatba && k.platba !== filtrPlatba) return false;
      if (filtrLokalita && !(k.lokality || []).includes(filtrLokalita)) return false;
      if (!q) return true;
      return bezDiakritiky(`${k.jmeno} ${k.email} ${k.telefon}`).includes(q);
    });
  }, [klienti, search, filtrPlatba, filtrLokalita]);

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Users}
        nazev="Klienti bootcampů"
        podtitulek={jenMujBeh ? "jen vaše běhy" : undefined}
        odznak={data ? filtrovani.length : false}
        napoveda="Každý, kdo se přihlásil na bootcamp. Klepnutím na řádek se otevře detail s komentáři, docházkou a fakturami. Odkaz na konkrétní klientku najdeš v hlavičce jejího detailu."
        akce={
          <button
            type="button"
            onClick={refetch}
            disabled={loading || refreshing}
            title="Obnovit"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 disabled:opacity-60 sm:hover:bg-white/25"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        }
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat jméno, e-mail, telefon…"
            className="field w-full pl-9 pr-3" />
        </div>
        {lokality.length > 1 && (
          <select value={filtrLokalita} onChange={(e) => setFiltrLokalita(e.target.value)}
            className="field w-auto">
            <option value="">Lokalita: vše</option>
            {lokality.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        <select value={filtrPlatba} onChange={(e) => setFiltrPlatba(e.target.value)}
          className="field w-auto">
          <option value="">Platba: vše</option>
          <option value="zaplaceno">zaplaceno</option>
          <option value="ceka na platbu">čeká na platbu</option>
          <option value="po splatnosti">po splatnosti</option>
          <option value="bez faktury">bez faktury</option>
        </select>
      </div>

      {loading && !data ? (
        <div className="p-3 sm:p-4"><SkeletonRows rows={6} /></div>
      ) : filtrovani.length === 0 ? (
        <EmptyState
          icon={Users}
          title={klienti.length > 0 ? "Filtru neodpovídá nikdo" : "Zatím tu nikdo není"}
          hint={klienti.length > 0
            ? "Zkus jiný filtr nebo vymaž hledání."
            : jenMujBeh
              ? "Nemáte přiřazený žádný běh s přihlášenými účastnicemi. Vidíte jen lidi z běhů, kde jste vedený jako trenér."
              : "Objeví se tu každý, kdo se přihlásí na bootcamp."}
        />
      ) : uzka ? (
        /* 13.9.2026 (fáze 4k): na telefonu seznam řádků. Sedmisloupcová tabulka
           se na 390 px nevešla a vyjížděla mimo obrazovku. Na počítači zůstává. */
        <div className="divide-y divide-slate-100">
          {filtrovani.map((k) => (
            <button
              key={k.klic}
              type="button"
              onClick={() => otevri(k.klic)}
              className="block w-full px-4 py-3 text-left sm:hover:bg-brand-50/60"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-bold text-slate-800">{k.jmeno}</span>
                <Platba stav={k.platba} />
              </div>
              {(k.telefon || k.email) && (
                <div className="mt-0.5 truncate text-xs tabular-nums text-slate-500">
                  {[k.telefon ? telCz(k.telefon) : null, k.email].filter(Boolean).join(", ")}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                  {k.pocetBehu} {k.pocetBehu === 1 ? "běh" : k.pocetBehu < 5 ? "běhy" : "běhů"}
                </span>
                {(k.lokality || []).map((l) => (
                  <span key={l} className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{l}</span>
                ))}
              </div>
              {k.posledniBeh && (
                <div className="mt-1.5 truncate text-xs text-slate-500">
                  {k.posledniBeh.nazev}, {k.posledniBeh.tarif || "bez tarifu"}
                  {k.posledniBeh.vstupy == null ? ", bez omezení" : `, ${k.posledniBeh.vstupy} vstupů`}
                </div>
              )}
              {k.maPoznamku && (
                <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-600">
                  <StickyNote className="mt-px h-3.5 w-3.5 shrink-0 text-brand-500" />
                  <span className="line-clamp-2">{k.poznamka}</span>
                  {k.pocetKomentaru > 1 && (
                    <span className="shrink-0 rounded-full bg-brand-100 px-1.5 text-[11px] font-bold text-brand-800">{k.pocetKomentaru}</span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto p-3 sm:p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Jméno</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Kontakt</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Běhy</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Lokalita</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Poslední běh</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Platby</th>
                <th className="text-left font-bold px-3 py-2 border-b border-slate-200">Komentáře</th>
              </tr>
            </thead>
            <tbody>
              {filtrovani.map((k) => (
                <tr key={k.klic} onClick={() => otevri(k.klic)}
                  className="cursor-pointer hover:bg-brand-50/60 border-b border-slate-50 last:border-none">
                  <td className="px-3 py-2.5 font-bold text-slate-800">{k.jmeno}</td>
                  <td className="px-3 py-2.5 text-[11.5px] text-slate-500 tabular-nums">
                    {k.telefon && <div>{telCz(k.telefon)}</div>}
                    {k.email && <div className="truncate max-w-[220px]">{k.email}</div>}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{k.pocetBehu}</td>
                  <td className="px-3 py-2.5 text-slate-700">
                    {(k.lokality || []).length ? k.lokality.join(", ") : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {k.posledniBeh ? (
                      <>
                        <div className="text-slate-700">{k.posledniBeh.nazev}</div>
                        <div className="text-[11.5px] text-slate-400">
                          {k.posledniBeh.tarif || "—"}
                          {k.posledniBeh.vstupy == null ? ", bez omezení" : `, ${k.posledniBeh.vstupy} vstupů`}
                        </div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5"><Platba stav={k.platba} /></td>
                  <td className="px-3 py-2.5 max-w-[260px]">
                    {k.maPoznamku ? (
                      <span className="inline-flex items-start gap-1.5 text-[11.5px] text-slate-600" title={k.poznamka}>
                        <StickyNote className="w-3.5 h-3.5 text-brand-500 shrink-0 mt-px" />
                        <span className="line-clamp-2">{k.poznamka}</span>
                        {k.pocetKomentaru > 1 && (
                          <span className="shrink-0 rounded-full bg-brand-100 text-brand-800 px-1.5 text-[11px] font-bold">{k.pocetKomentaru}</span>
                        )}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Portál do body (1. 11. 2026): karta seznamu má .glass s backdrop-filter,
          ten dělá z karty obal pro position: fixed a okno detailu se v ní
          ořezávalo na výšku seznamu. */}
      {otevreny && createPortal(
        <DetailKlienta klic={otevreny} onClose={() => otevri(null)} onZmena={refetch} />,
        document.body
      )}
    </KartaPrehledu>
  );
}
